import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  redirect,
} from '@tanstack/react-router'
import { useOperatorSession } from '@/store/useOperatorSession'

// Layouts (lazy-loaded to keep bundle small)
import { lazy, Suspense } from 'react'

const LoginPage = lazy(() => import('@/pages/login/LoginPage'))
const DashboardPage = lazy(() => import('@/pages/dashboard/DashboardPage'))
const TenantsPage = lazy(() => import('@/pages/tenants/TenantsPage'))
const TenantDetailPage = lazy(() => import('@/pages/tenants/TenantDetailPage'))
const UsersPage = lazy(() => import('@/pages/users/UsersPage'))
const UserDetailPage = lazy(() => import('@/pages/users/UserDetailPage'))
const WorkflowsPage = lazy(() => import('@/pages/workflows/WorkflowsPage'))
const EventAnalyticsPage = lazy(() => import('@/pages/event-analytics/EventAnalyticsPage'))
const EventAnalyticsSettingsPage = lazy(() => import('@/pages/event-analytics/EventAnalyticsSettingsPage'))
const OperatorsPage = lazy(() => import('@/pages/operators/OperatorsPage'))
const HostsPage = lazy(() => import('@/pages/hosts/HostsPage'))
const HostDetailPage = lazy(() => import('@/pages/hosts/HostDetailPage'))
const InstanceConfigPage = lazy(() => import('@/pages/instance/InstanceConfigPage'))
const StorageConfigPage = lazy(() => import('@/pages/storage/StorageConfigPage'))
const ShellPage = lazy(() => import('@/pages/shell/ShellPage'))
const AuthSettingsPage = lazy(() => import('@/pages/auth-settings/AuthSettingsPage'))
const AuthProvidersPage = lazy(() => import('@/pages/auth-providers/AuthProvidersPage'))
const MailConfigPage = lazy(() => import('@/pages/mail/MailConfigPage'))
const MailLayoutsPage = lazy(() => import('@/pages/mail/MailLayoutsPage'))
const MailTemplatesPage = lazy(() => import('@/pages/mail/MailTemplatesPage'))
const MailLogsPage = lazy(() => import('@/pages/mail/MailLogsPage'))
const RolesPage = lazy(() => import('@/pages/roles/RolesPage'))
const CreditPolicyPage = lazy(() => import('@/pages/llm/CreditPolicyPage'))
const ProxiesPage = lazy(() => import('@/pages/llm/ProxiesPage'))
const WebSearchPage = lazy(() => import('@/pages/llm/WebSearchPage'))
const GeneralSettingsPage = lazy(() => import('@/pages/llm/GeneralSettingsPage'))
const RateLimitsPage = lazy(() => import('@/pages/llm/RateLimitsPage'))
const SkillsPage = lazy(() => import('@/pages/llm/SkillsPage'))
const RagPipelinePage = lazy(() => import('@/pages/llm/RagPipelinePage'))
const ChattyAgentsPage = lazy(() => import('@/pages/chatty/ChattyAgentsPage'))
const AlertsPage = lazy(() => import('@/pages/llm/AlertsPage'))
const UsagePage = lazy(() => import('@/pages/llm/UsagePage'))
const RequestsPage = lazy(() => import('@/pages/llm/RequestsPage'))
const RequestDetailPage = lazy(() => import('@/pages/llm/RequestDetailPage'))
const CatalogPage = lazy(() => import('@/pages/llm/CatalogPage'))
const PriceDatasetsPage = lazy(() => import('@/pages/llm/PriceDatasetsPage'))
const EntitlementsAddonsPage = lazy(() => import('@/pages/entitlements/AddonsPage'))
const AddonEditorPage = lazy(() => import('@/pages/entitlements/AddonEditorPage'))
const EntitlementsFeatureCatalogPage = lazy(() => import('@/pages/entitlements/FeatureCatalogPage'))
const EntitlementsTariffsPage = lazy(() => import('@/pages/entitlements/TariffsPage'))
const EntitlementDetailPage = lazy(() => import('@/pages/entitlements/TariffDetailPage'))
const BillingOperationsPage = lazy(() => import('@/pages/billing/BillingOperationsPage'))
const BillingProviderSettingsPage = lazy(() => import('@/pages/billing/BillingProviderSettingsPage'))
const PublicBugtrackerSettingsPage = lazy(() => import('@/pages/public-bugtracker/PublicBugtrackerSettingsPage'))
const PublicBugtrackerReportsPage = lazy(() => import('@/pages/public-bugtracker/PublicBugtrackerReportsPage'))
const BiDwhInstancesPage = lazy(() => import('@/pages/bi-platform/DwhInstancesPage'))
const RouterSettingsPage = lazy(() => import('@/pages/chatty-router/RouterSettingsPage'))

/** Root route — wraps everything */
const rootRoute = createRootRoute({
  component: () => (
    <Suspense fallback={<div className="flex h-screen items-center justify-center text-muted-foreground">Loading…</div>}>
      <Outlet />
    </Suspense>
  ),
})

/** Guard component — redirects unauthenticated users to /login */
function AuthGuard() {
  const token = useOperatorSession((s) => s.token)
  if (!token) {
    const path = window.location.pathname + window.location.search
    window.location.href = `/login?redirect=${encodeURIComponent(path)}`
    return null
  }
  return <Outlet />
}

const guardRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'guard',
  component: AuthGuard,
})

function requireSuperadminRoute() {
  if (useOperatorSession.getState().operator?.role !== 'superadmin') {
    throw redirect({ to: '/' })
  }
}

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: () => <LoginPage />,
})

const dashboardRoute = createRoute({
  getParentRoute: () => guardRoute,
  path: '/',
  component: () => <DashboardPage />,
})

const tenantsRoute = createRoute({
  getParentRoute: () => guardRoute,
  path: '/tenants',
  component: () => <TenantsPage />,
})

const tenantDetailRoute = createRoute({
  getParentRoute: () => guardRoute,
  path: '/tenants/$tenantId',
  component: () => <TenantDetailPage />,
})

const tenantDetailTabRoute = createRoute({
  getParentRoute: () => guardRoute,
  path: '/tenants/$tenantId/$tab',
  component: () => <TenantDetailPage />,
})

const usersRoute = createRoute({
  getParentRoute: () => guardRoute,
  path: '/users',
  component: () => <UsersPage />,
})

const userDetailRoute = createRoute({
  getParentRoute: () => guardRoute,
  path: '/users/$userId',
  component: () => <UserDetailPage />,
})

const workflowsRoute = createRoute({
  getParentRoute: () => guardRoute,
  path: '/workflows',
  component: () => <WorkflowsPage />,
})

const eventAnalyticsRoute = createRoute({
  getParentRoute: () => guardRoute,
  path: '/event-analytics',
  component: () => <EventAnalyticsPage />,
})

const eventAnalyticsEventsRoute = createRoute({
  getParentRoute: () => guardRoute,
  path: '/event-analytics/events',
  component: () => <EventAnalyticsPage />,
})

const eventAnalyticsSettingsRoute = createRoute({
  getParentRoute: () => guardRoute,
  path: '/event-analytics/settings',
  component: () => <EventAnalyticsSettingsPage />,
})

const operatorsRoute = createRoute({
  getParentRoute: () => guardRoute,
  path: '/operators',
  component: () => <OperatorsPage />,
})

const hostsRoute = createRoute({
  getParentRoute: () => guardRoute,
  path: '/hosts',
  component: () => <HostsPage />,
})

const hostDetailRoute = createRoute({
  getParentRoute: () => guardRoute,
  path: '/hosts/$hostId',
  component: () => <HostDetailPage />,
})

const instanceRoute = createRoute({
  getParentRoute: () => guardRoute,
  path: '/instance',
  component: () => <InstanceConfigPage />,
})

const storageRoute = createRoute({
  getParentRoute: () => guardRoute,
  path: '/storage',
  component: () => <StorageConfigPage />,
})

const shellRoute = createRoute({
  getParentRoute: () => guardRoute,
  path: '/shell',
  beforeLoad: requireSuperadminRoute,
  component: () => <ShellPage />,
})

const authSettingsRoute = createRoute({
  getParentRoute: () => guardRoute,
  path: '/auth-settings',
  component: () => <AuthSettingsPage />,
})

const authProvidersRoute = createRoute({
  getParentRoute: () => guardRoute,
  path: '/auth-providers',
  component: () => <AuthProvidersPage />,
})

const mailConfigRoute = createRoute({
  getParentRoute: () => guardRoute,
  path: '/mail/config',
  beforeLoad: requireSuperadminRoute,
  component: () => <MailConfigPage />,
})

const mailTemplatesRoute = createRoute({
  getParentRoute: () => guardRoute,
  path: '/mail/templates',
  beforeLoad: requireSuperadminRoute,
  component: () => <MailTemplatesPage />,
})

const mailLayoutsRoute = createRoute({
  getParentRoute: () => guardRoute,
  path: '/mail/layouts',
  beforeLoad: requireSuperadminRoute,
  component: () => <MailLayoutsPage />,
})

const mailLogsRoute = createRoute({
  getParentRoute: () => guardRoute,
  path: '/mail/logs',
  beforeLoad: requireSuperadminRoute,
  component: () => <MailLogsPage />,
})

const rolesRoute = createRoute({
  getParentRoute: () => guardRoute,
  path: '/roles',
  component: () => <RolesPage />,
})

const llmCreditPolicyRoute = createRoute({
  getParentRoute: () => guardRoute,
  path: '/llm/credit-policy',
  component: () => <CreditPolicyPage />,
})

const llmProxiesRoute = createRoute({
  getParentRoute: () => guardRoute,
  path: '/llm/proxies',
  component: () => <ProxiesPage />,
})

const llmWebSearchRoute = createRoute({
  getParentRoute: () => guardRoute,
  path: '/llm/web-search',
  beforeLoad: requireSuperadminRoute,
  component: () => <WebSearchPage />,
})

const llmGeneralSettingsRoute = createRoute({
  getParentRoute: () => guardRoute,
  path: '/llm/general-settings',
  beforeLoad: requireSuperadminRoute,
  component: () => <GeneralSettingsPage />,
})

const llmRateLimitsRoute = createRoute({
  getParentRoute: () => guardRoute,
  path: '/llm/rate-limits',
  component: () => <RateLimitsPage />,
})

const llmRagPipelineRoute = createRoute({
  getParentRoute: () => guardRoute,
  path: '/llm/rag-pipeline',
  component: () => <RagPipelinePage />,
})

const chattyAgentsRoute = createRoute({
  getParentRoute: () => guardRoute,
  path: '/chatty-agents',
  component: () => <ChattyAgentsPage />,
})

const llmAlertsRoute = createRoute({
  getParentRoute: () => guardRoute,
  path: '/llm/alerts',
  component: () => <AlertsPage />,
})

const llmUsageRoute = createRoute({
  getParentRoute: () => guardRoute,
  path: '/llm/usage',
  component: () => <UsagePage />,
})

const llmRequestsRoute = createRoute({
  getParentRoute: () => guardRoute,
  path: '/llm/requests',
  component: () => <RequestsPage />,
})

const llmRequestDetailRoute = createRoute({
  getParentRoute: () => guardRoute,
  path: '/llm/requests/$requestId',
  component: () => <RequestDetailPage />,
})

const llmCatalogRoute = createRoute({
  getParentRoute: () => guardRoute,
  path: '/llm/catalog',
  component: () => <CatalogPage />,
})

const llmPriceDatasetsRoute = createRoute({
  getParentRoute: () => guardRoute,
  path: '/llm/price-datasets',
  component: () => <PriceDatasetsPage />,
})

const entitlementsFeatureRoute = createRoute({
  getParentRoute: () => guardRoute,
  path: '/entitlements/features',
  component: () => <EntitlementsFeatureCatalogPage />,
})

const entitlementsTariffsRoute = createRoute({
  getParentRoute: () => guardRoute,
  path: '/entitlements/tariffs',
  component: () => <EntitlementsTariffsPage />,
})

const entitlementsTariffDetailRoute = createRoute({
  getParentRoute: () => guardRoute,
  path: '/entitlements/tariffs/$tariffId',
  component: () => <EntitlementDetailPage />,
})

const entitlementsAddonsRoute = createRoute({
  getParentRoute: () => guardRoute,
  path: '/entitlements/addons',
  component: () => <EntitlementsAddonsPage />,
})

const entitlementsAddonCreateRoute = createRoute({
  getParentRoute: () => guardRoute,
  path: '/entitlements/addons/new',
  component: () => <AddonEditorPage />,
})

const entitlementsAddonDetailRoute = createRoute({
  getParentRoute: () => guardRoute,
  path: '/entitlements/addons/$addonSkuId',
  component: () => <AddonEditorPage />,
})

const billingOperationsRoute = createRoute({
  getParentRoute: () => guardRoute,
  path: '/billing',
  component: () => <BillingOperationsPage />,
})

const billingProviderSettingsRoute = createRoute({
  getParentRoute: () => guardRoute,
  path: '/billing/provider-settings',
  component: () => <BillingProviderSettingsPage />,
})

const publicBugtrackerSettingsRoute = createRoute({
  getParentRoute: () => guardRoute,
  path: '/public-bugtracker/settings',
  component: () => <PublicBugtrackerSettingsPage />,
})

const publicBugtrackerReportsRoute = createRoute({
  getParentRoute: () => guardRoute,
  path: '/public-bugtracker/reports',
  component: () => <PublicBugtrackerReportsPage />,
})

const biDwhInstancesRoute = createRoute({
  getParentRoute: () => guardRoute,
  path: '/bi-platform/dwh-instances',
  component: () => <BiDwhInstancesPage />,
})

const chattyRouterSettingsRoute = createRoute({
  getParentRoute: () => guardRoute,
  path: '/chatty-router',
  component: () => <RouterSettingsPage />,
})

const llmSkillsRoute = createRoute({ getParentRoute: () => guardRoute, path: '/llm/skills', component: () => <SkillsPage /> })

const routeTree = rootRoute.addChildren([
  loginRoute,
  guardRoute.addChildren([
    dashboardRoute,
    tenantsRoute,
    tenantDetailRoute,
    tenantDetailTabRoute,
    usersRoute,
    userDetailRoute,
    workflowsRoute,
    eventAnalyticsRoute,
    eventAnalyticsEventsRoute,
    eventAnalyticsSettingsRoute,
    hostsRoute,
    hostDetailRoute,
    operatorsRoute,
    instanceRoute,
    storageRoute,
    shellRoute,
    authSettingsRoute,
    authProvidersRoute,
    mailConfigRoute,
    mailLayoutsRoute,
    mailTemplatesRoute,
    mailLogsRoute,
    rolesRoute,
    llmCreditPolicyRoute,
    llmProxiesRoute,
    llmWebSearchRoute,
    llmGeneralSettingsRoute,
    llmRateLimitsRoute,
    llmRagPipelineRoute,
    llmSkillsRoute,
    chattyAgentsRoute,
    llmAlertsRoute,
    llmUsageRoute,
    llmRequestsRoute,
    llmRequestDetailRoute,
    llmCatalogRoute,
    llmPriceDatasetsRoute,
    entitlementsFeatureRoute,
    entitlementsTariffsRoute,
    entitlementsTariffDetailRoute,
    entitlementsAddonsRoute,
    entitlementsAddonCreateRoute,
    entitlementsAddonDetailRoute,
    billingOperationsRoute,
    billingProviderSettingsRoute,
    publicBugtrackerSettingsRoute,
    publicBugtrackerReportsRoute,
    biDwhInstancesRoute,
    chattyRouterSettingsRoute,
  ]),
])

export const router = createRouter({ routeTree })

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}
